/**
 * usePathEngine.ts
 *
 * Handles the lifecycle of career path enrollment and removal.
 * Plain exported object — no React hooks, safe to call from anywhere.
 *
 * onPathEnrolled  — called after a path is added to the student's profile.
 *                   Creates a journey record and seeds initial goals.
 *
 * onPathRemoved   — called when a student removes a path.
 *                   Cleans up journey records, linked goals, and localStorage cache.
 */

import { AuthStore } from '@/hooks/stores/AuthStore';
import { StorageService } from '@/hooks/services/storageService';
import { careerPathApi } from '@/hooks/api/careerPathApi';
import { goalEngine } from '@/hooks/useGoalEngine';

export const pathEngine = {
  // ── Enroll ──────────────────────────────────────────────────────────────────

  /**
   * Called when a student adds a broad/field/spec path.
   * 1. Fetches content so we know the module count and first module id
   * 2. Creates a UserJourneyRecord via careerPathApi.enroll
   * 3. Seeds goals via goalEngine.onPathAdded
   */
  async onPathEnrolled(
    pathId: string,
    pathName: string,
    pathLevel: 'broad' | 'field' | 'specialization' = 'broad',
  ): Promise<void> {
    try {
      const user = AuthStore.getState().currentUser;
      if (!user) return;

      // Fetch content to get module metadata
      const content = await careerPathApi.getContent(pathId);
      const firstModule = content.modules.find((m) => m.order === 1) ?? content.modules[0];

      // Create journey record
      await careerPathApi.enroll(
        user._id,
        pathId,
        pathLevel,
        pathName,
        content.modules.length,
        firstModule?.id ?? '',
      );

      // Seed goals (goalEngine handles deduplication)
      goalEngine.onPathAdded(pathId, pathName);
    } catch (err) {
      console.error('[pathEngine] onPathEnrolled error:', err);
    }
  },

  // ── Remove ───────────────────────────────────────────────────────────────────

  /**
   * Called when a student removes a career path.
   * 1. Removes all journey records for this path (and any child paths)
   * 2. Removes all auto-generated goals linked to this path
   * 3. Clears journey_progress from localStorage if it pointed to this path
   */
  onPathRemoved(pathId: string): void {
    try {
      const user = AuthStore.getState().currentUser;
      if (!user) return;

      // 1. Delete journey records
      careerPathApi.deleteJourneysForPath(user._id, pathId);

      // 2. Remove linked goals from StorageService
      const allGoals = StorageService.read('goals');
      if (Array.isArray(allGoals)) {
        const remaining = allGoals.filter(
          (g: any) =>
            // Keep goals that are NOT auto-generated for this path
            !(g.userId === user._id && g.autoGenerated === true && g.linkedPathId === pathId)
        );
        StorageService.write('goals', remaining);
      }

      // 3. journey_progress cleanup is handled inside careerPathApi.deleteJourneysForPath
    } catch (err) {
      console.error('[pathEngine] onPathRemoved error:', err);
    }
  },

  // ── Module complete (convenience passthrough) ─────────────────────────────

  /**
   * Called when a student marks a module complete in Journey.jsx.
   * Updates the journey record and fires goalEngine triggers.
   */
  async onModuleComplete(
    pathId: string,
    moduleId: string,
    moduleTitle: string,
    quizScore?: number,
    xpEarned = 40,
    traitDeltas?: { traitId: string; delta: number }[],
  ): Promise<void> {
    try {
      const user = AuthStore.getState().currentUser;
      if (!user) return;

      await careerPathApi.completeModule(user._id, pathId, moduleId, quizScore, xpEarned);
      goalEngine.onModuleComplete(pathId);

      // Update journey_progress title with real module title
      try {
        const raw = localStorage.getItem('journey_progress');
        if (raw) {
          const jp = JSON.parse(raw);
          localStorage.setItem('journey_progress', JSON.stringify({
            ...jp,
            lastLessonTitle: moduleTitle,
          }));
        }
      } catch { /* ignore */ }
    } catch (err) {
      console.error('[pathEngine] onModuleComplete error:', err);
    }
  },

  // ── Navigation update ─────────────────────────────────────────────────────

  /** Called when the student navigates to a different module (without completing it). */
  async onModuleViewed(pathId: string, moduleId: string): Promise<void> {
    try {
      const user = AuthStore.getState().currentUser;
      if (!user) return;
      await careerPathApi.updateCurrentModule(user._id, pathId, moduleId);
    } catch {
      // non-critical — don't surface errors for navigation events
    }
  },
};
